import CategoriesIcon from '@assets/icons/categories.svg';
import i18n from '@/utils/i18n';
import PencilIcon from '@assets/icons/pencil-thin.svg';
import PrinterIcon from '@assets/icons/printer.svg';
import React, { useMemo } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, SCREEN_PADDING, shadow, TYPOGRAPHY } from '@/styles';
import { List } from '@/components/list';
import {
  ListDisplaySize,
  MULTI_ORDER_GUIDE_ITEM_SORT_ORDERS,
  useOrderGuideSettingsContext,
} from '@/contexts/order-guide-settings-context';
import { ListOption } from '@/components/list-item';
import { ListViewToggle } from '@/components/list-view-toggle';
import { ORDER_GUIDE_ROUTES } from '@/navigators/routes';
import { OrderGuideStackParams } from '@/navigators/types';
import { SortOrderSelectList } from '@/components/orders/sort-order-select-list';
import { StackNavigationProp } from '@react-navigation/stack';
import { SvgProps } from 'react-native-svg';
import { Switch } from '@/components/switch';
import {
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNavigation } from '@react-navigation/native';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOpenParSheetPDF } from '@/hooks/use-open-par-sheet-pdf';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';

type Navigation = StackNavigationProp<
  OrderGuideStackParams,
  ORDER_GUIDE_ROUTES.Scan
>;

interface Props {
  onClose: () => void;
}

interface SettingListOptionMetadata {
  Icon: React.FC<SvgProps>;
  iconProps: SvgProps;
}
type SettingListOption = ListOption<SettingListOptionMetadata>;

const ORDERED_LIST_DISPLAY_SIZES = [
  ListDisplaySize.Condensed,
  ListDisplaySize.Standard,
  ListDisplaySize.Large,
];

enum SettingsActionSheetRouteKey {
  DisplaySettings = 'DISPLAY_SETTINGS',
  Menu = 'MENU',
}

enum MenuOptions {
  DisplaySettings = 'DisplaySettings',
  Edit = 'Edit',
  PrintPARSheet = 'PrintParSheet',
}

enum SettingsOptions {
  ListDisplaySizePicker = 'LIST_DISPLAY_SIZE_PICKER',
  ListGridView = 'LIST_GRID_VIEW',
  ShowCategoryFilter = 'SHOW_CATEGORY_FILTER',
  SortOrder = 'SORT_ORDER',
}

export const OrderGuideSettingsActionSheet: React.FC<Props> = ({ onClose }) => {
  const [styles, theme] = useStyles();
  const navigation = useNavigation<Navigation>();
  const { isSmallLayout, isSmallTabletLayout } = useLayoutContext();
  const { partitionFilter, selectedCollection } = useOrderGuideItemsContext();
  const { customOrderGuideEditingEnabled } = useNewOrderContext();
  const { height } = useWindowDimensions();
  const {
    listDisplaySize,
    orderGuideSortOrder,
    setListDisplaySize,
    setOrderGuideSortOrder,
    setSelectedCollectionUUID,
    setShowCategoryFilters,
    showCategoryFilters,
  } = useOrderGuideSettingsContext();
  const { openParSheetPDF, loadingParSheetPDF } = useOpenParSheetPDF();

  const printParSheetEnabled = useDynamicConfigValue('print_par_sheet_enabled');

  // Max height of the action sheet is 85% of the screen to enable scrolling
  // + still have room to dismiss the modal
  const actionSheetMaxHeight = Math.floor(height * 0.85);

  const orderGuideEditingEnabled = Boolean(
    customOrderGuideEditingEnabled &&
      selectedCollection &&
      !selectedCollection.isLocked,
  );

  const menuOptions = useMemo<SettingListOption[]>(() => {
    const options = [];

    if (orderGuideEditingEnabled) {
      options.push({
        metadata: {
          Icon: PencilIcon,
          iconProps: {
            stroke: theme.colors.primary,
          },
        },
        title: i18n.t('order_guide_settings.edit', {
          name: selectedCollection?.name,
        }),
        value: MenuOptions.Edit,
      });
    }

    if (printParSheetEnabled && selectedCollection) {
      options.push({
        metadata: {
          Icon: PrinterIcon,
          iconProps: {
            stroke: theme.sk.color.icon.primary,
          },
        },
        title: i18n.t('order_guide_settings.print_par_sheet'),
        value: MenuOptions.PrintPARSheet,
      });
    }

    options.push({
      metadata: {
        Icon: CategoriesIcon,
        iconProps: {
          stroke: theme.colors.primary,
        },
      },
      title: i18n.t('order_guide_settings.display_options'),
      value: MenuOptions.DisplaySettings,
    });

    return options;
  }, [
    orderGuideEditingEnabled,
    printParSheetEnabled,
    selectedCollection,
    theme,
  ]);

  const displaySettingsListOptions = useMemo(() => {
    const baseOptions = [
      {
        title: i18n.t('sort_list_by'),
        value: SettingsOptions.SortOrder,
      },
      {
        title: i18n.t('show_category_filter'),
        value: SettingsOptions.ShowCategoryFilter,
      },
      {
        title: i18n.t('list_display'),
        value: SettingsOptions.ListDisplaySizePicker,
      },
    ];

    return !isSmallLayout && !isSmallTabletLayout
      ? [
          {
            title: i18n.t('layout'),
            value: SettingsOptions.ListGridView,
          },
          ...baseOptions,
        ]
      : baseOptions;
  }, [isSmallLayout, isSmallTabletLayout]);

  return (
    <CombinedActionSheet
      contentStyle={[
        styles.container,
        isSmallLayout
          ? { maxHeight: actionSheetMaxHeight }
          : styles.containerLarge,
      ]}
      hasStickyHeader={false}
      initialRoute={SettingsActionSheetRouteKey.Menu}
      onClose={onClose}
      renderScene={({ routeKey, jumpTo }) => {
        switch (routeKey) {
          case SettingsActionSheetRouteKey.Menu:
            return (
              <List
                data={menuOptions}
                onSelect={(option) => {
                  switch (option.value) {
                    case MenuOptions.Edit:
                      if (selectedCollection) {
                        navigation.push(ORDER_GUIDE_ROUTES.Edit, {
                          collectionUUID: selectedCollection.uuid,
                          initialSelectedPartitionUUID: partitionFilter
                            ? String(partitionFilter.value)
                            : null,
                          setSelectedCollectionUUID,
                        });
                        onClose();
                      }
                      return;
                    case MenuOptions.PrintPARSheet: {
                      if (selectedCollection && !loadingParSheetPDF) {
                        openParSheetPDF(selectedCollection.uuid);
                      }
                      return;
                    }
                    case MenuOptions.DisplaySettings: {
                      jumpTo(SettingsActionSheetRouteKey.DisplaySettings);
                      return;
                    }
                  }
                }}
                renderLeftComponent={(option) => {
                  if (option.metadata) {
                    return (
                      <option.metadata.Icon {...option.metadata.iconProps} />
                    );
                  }
                }}
                renderRightComponent={(option) => {
                  if (
                    option.value === MenuOptions.PrintPARSheet &&
                    loadingParSheetPDF
                  ) {
                    return <ActivityIndicator size="small" />;
                  }
                }}
              />
            );
          case SettingsActionSheetRouteKey.DisplaySettings:
            return (
              <List
                data={displaySettingsListOptions}
                listItemTitleStyle={[TYPOGRAPHY.large, FONTS.primarySemiBold]}
                renderListItemFooter={({ value }) => {
                  if (value === SettingsOptions.SortOrder) {
                    return (
                      <View style={styles.sortList}>
                        <SortOrderSelectList
                          ItemSeparatorComponent={null}
                          onChange={(nextValue) => {
                            setOrderGuideSortOrder(nextValue);
                          }}
                          supportedSortOptions={
                            MULTI_ORDER_GUIDE_ITEM_SORT_ORDERS
                          }
                          type="radio"
                          value={orderGuideSortOrder}
                        />
                      </View>
                    );
                  }

                  if (value === SettingsOptions.ListDisplaySizePicker) {
                    return (
                      <View style={styles.sizeOptionFooter}>
                        {ORDERED_LIST_DISPLAY_SIZES.map((sizeOption) => (
                          <TouchableOpacity
                            key={sizeOption}
                            onPress={() => {
                              setListDisplaySize(sizeOption);
                            }}
                            style={[
                              styles.sizeOptionItem,
                              listDisplaySize === sizeOption
                                ? styles.sizeOptionItemSelected
                                : null,
                            ]}
                          >
                            <Text
                              style={[TYPOGRAPHY.xsmall, styles.sizeOptionText]}
                            >
                              {i18n.t(
                                `list_display_size.${sizeOption.toLowerCase()}`,
                              )}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    );
                  }

                  return null;
                }}
                renderRightComponent={({ value }) => {
                  if (value === SettingsOptions.ShowCategoryFilter) {
                    return (
                      <Switch
                        onValueChange={setShowCategoryFilters}
                        testID="category-filter-switch"
                        trackingData={{
                          name: 'order_guide_settings.show_category_filter_switch',
                        }}
                        value={showCategoryFilters}
                      />
                    );
                  } else if (value === SettingsOptions.ListGridView) {
                    return (
                      <ListViewToggle
                        style={styles.listViewToggle}
                        type="dark"
                      />
                    );
                  }
                  return null;
                }}
                style={isSmallLayout ? styles.list : null}
              />
            );
        }
      }}
      routes={{
        [SettingsActionSheetRouteKey.Menu]: {
          previousKey: null,
          title: i18n.t('order_guide_settings'),
        },
        [SettingsActionSheetRouteKey.DisplaySettings]: {
          previousKey: SettingsActionSheetRouteKey.Menu,
          showSmallLayoutBackButton: true,
          showSmallLayoutTitle: true,
          title: i18n.t('order_guide_settings.display_options.title'),
        },
      }}
      titleStyle={styles.title}
    />
  );
};

const FOOTER_BORDER_RADIUS = 48;
const FOOTER_PADDING = 4;

const useStyles = createStyles((theme) => ({
  container: {
    paddingHorizontal: 0,
  },
  containerLarge: {
    paddingTop: 0,
  },
  list: {
    // Reduce whitespace at the top of the action sheet since there's no title
    marginTop: -16,
  },
  listViewToggle: {
    borderLeftWidth: 0,
    paddingHorizontal: 0,
  },
  sizeOptionFooter: {
    backgroundColor: theme.colors.stroke,
    borderRadius: FOOTER_BORDER_RADIUS,
    flexDirection: 'row',
    gap: 4,
    marginTop: 16,
    padding: FOOTER_PADDING,
  },
  sizeOptionItem: {
    borderRadius: FOOTER_BORDER_RADIUS - FOOTER_PADDING,
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    paddingVertical: 6,
  },
  sizeOptionItemSelected: {
    ...shadow({
      color: theme.colors.black,
      elevation: 1,
      opacity: 0.15,
      radius: 4,
      x: 0,
      y: 2,
    }),
    backgroundColor: theme.colors.white,
  },
  sizeOptionText: {
    ...FONTS.primarySemiBold,
    lineHeight: 14,
  },
  sortList: {
    paddingTop: SCREEN_PADDING / 2,
  },
  title: {
    paddingHorizontal: SCREEN_PADDING,
  },
}));
